// Shared helpers for a protocol's primary sites (the `primarySites[]` collection).
//
// A protocol's site used to be a single free-text string (`primarySite`). It is
// now a collection of links to `primary_site_master`, exactly one of which is
// flagged as the lead. The old scalar still comes back on reads as a read-only
// mirror of the lead site's name, but it is deprecated and IGNORED ON WRITE —
// nothing in this app should read or send it any more.
//
// See docs/REACT_TEAM_SPEC_protocol_primary_site.md.
//
// THE ONE THING THAT MUST NOT GO WRONG (spec §5.1). On POST /protocol/saveUpdate
// the `primarySites` key has three distinct meanings:
//
//     key omitted   -> existing links left untouched
//     []            -> ALL links deleted for that protocol
//     [{...}]       -> link set replaced with exactly what was sent
//
// An accidental `[]` destroys data and still returns 200. That is why callers
// must never keep `primarySites` in a plain component-state initialiser and
// spread it unconditionally into a save body: build the key with
// buildPrimarySitesPayload() and only include it when the current sites were
// actually loaded from the server (or the user genuinely picked some).

import C from "./Config";

// GET /patientDiagnosis/primarySites — the picker's option list.
//
// This endpoint is Jackson-serialised and returns a PLAIN ARRAY, not the
// `{ statusCode, message: "<json string>" }` envelope that every /protocol/*
// and /userCase/* endpoint uses (spec §3). Do not route it through an envelope
// parser. Auth is injected globally by src/lib/fetchInterceptor.js.
export function fetchPrimarySiteOptions() {
  return fetch(C.getUrl() + "/patientDiagnosis/primarySites")
    .then((res) => {
      if (!res.ok) {
        throw new Error("Failed to fetch primary sites (" + res.status + ")");
      }
      return res.json();
    })
    .then((data) => (Array.isArray(data) ? data : []));
}

// GET /protocol/getProtocolsByPrimarySite?siteIds=1,2,3 — the protocols
// indicated for any of those sites (diagnosis spec §7). Match is an OR and each
// protocol comes back once however many of the requested sites it covers.
//
// UNLIKE fetchPrimarySiteOptions above, this one DOES use the
// `{ statusCode, message: "<json string>" }` envelope, so the payload has to be
// parsed out of `message`. The tolerant form is deliberate — it is the same
// shape used at ProtocolMaster.js:120 and survives either serialisation.
//
// `siteIdsParam` is the string from toSiteIdsParam(). An empty one means the
// parameter is OMITTED, which the backend reads as "no primary site is known"
// and answers with every active protocol — around 480 rows. That is the
// documented widening behaviour, not an error; callers must paginate.
export function fetchProtocolsByPrimarySite(siteIdsParam) {
  const query = siteIdsParam ? "?siteIds=" + encodeURIComponent(siteIdsParam) : "";
  return fetch(C.getUrl() + "/protocol/getProtocolsByPrimarySite" + query)
    .then((res) => {
      if (!res.ok) {
        throw new Error("Failed to fetch protocols (" + res.status + ")");
      }
      return res.json();
    })
    .then((res) => {
      if (Array.isArray(res)) return res;
      if (res && res.message) {
        const parsed = JSON.parse(res.message);
        return Array.isArray(parsed) ? parsed : [];
      }
      // 200 with an empty body is the "nothing matches" answer, not a failure:
      // the backend returns 200 [] here rather than the 451 "No record found."
      // used elsewhere on this controller, precisely so a picker can tell the
      // two apart (spec §7).
      return [];
    });
}

// Reads `primarySites[]` off a parsed protocol payload.
//
// Returns null — NOT [] — when the key is absent, so the caller can tell
// "the server never told us" apart from "this protocol has no sites". That
// distinction is what keeps a save from sending a destructive `[]`.
//
// Gson omits null fields entirely, so `'primarySites' in payload` is not a safe
// truth test (spec §3); check the value is an array instead.
export function readPrimarySites(protocolObj) {
  if (!protocolObj || !Array.isArray(protocolObj.primarySites)) {
    return null;
  }

  // Reads arrive already sorted for display: lead first, then alphabetical.
  // Preserve that order.
  return protocolObj.primarySites.map((site) => ({
    siteId: site.siteId,
    siteName: site.siteName,
    // Rides along on the payload; nothing renders it in this release (spec §6).
    tumorGroup: site.tumorGroup,
    leadSite: site.leadSite === true,
  }));
}

// Builds the request-body value: [{ siteId, leadSite }].
//
// The backend applies its own normalisation (spec §5.3) but each fallback is a
// silent one — entries with no siteId are skipped without an error, and if no
// entry is flagged as lead it promotes the first. Doing the same work here means
// those fallbacks never fire and the user's chosen lead is always the one that
// lands.
//
// Only `siteId` and `leadSite` are read by the backend; everything else is
// ignored, so nothing else is sent.
export function buildPrimarySitesPayload(selectedSites) {
  if (!Array.isArray(selectedSites)) {
    return [];
  }

  const seen = new Set();
  const cleaned = [];

  selectedSites.forEach((site) => {
    if (!site || site.siteId === null || site.siteId === undefined || site.siteId === "") {
      return; // would be silently dropped by the backend — drop it knowingly
    }
    const siteId = Number(site.siteId);
    if (Number.isNaN(siteId) || seen.has(siteId)) {
      return;
    }
    seen.add(siteId);
    cleaned.push({ siteId: siteId, leadSite: site.leadSite === true });
  });

  // Guarantee exactly one lead: first flagged entry wins, the rest are cleared,
  // and if nothing was flagged the first entry is promoted.
  let leadAssigned = false;
  cleaned.forEach((site) => {
    if (site.leadSite && !leadAssigned) {
      leadAssigned = true;
    } else {
      site.leadSite = false;
    }
  });
  if (!leadAssigned && cleaned.length > 0) {
    cleaned[0].leadSite = true;
  }

  return cleaned;
}

// Marks one selected site as the lead, clearing the flag on the others.
export function setLeadSite(selectedSites, siteId) {
  if (!Array.isArray(selectedSites)) {
    return [];
  }
  return selectedSites.map((site) => ({
    ...site,
    leadSite: Number(site.siteId) === Number(siteId),
  }));
}

// ---------------------------------------------------------------------------
// Diagnosis side.
//
// A patient diagnosis carries the same kind of collection, and the same
// three-state write contract on PUT /patientDiagnosis/{save,update}. See
// docs/REACT_TEAM_SPEC_patient_diagnosis_primary_site.md.
//
// It needs its own helpers rather than reusing the protocol ones above,
// because the two DTOs differ in three ways that all lose data if ignored:
//
//   1. A diagnosis site may have NO siteId. Sending just a name is legitimate
//      -- the backend resolves it against the master, case- and
//      whitespace-insensitively, and stores it as free text if nothing matches
//      (spec §4). buildPrimarySitesPayload drops those rows.
//   2. Rows carry diagnosisPrimarySiteId, which should be sent back when known
//      (spec §2). The protocol reader strips everything but id and name.
//   3. leadSite is BACKEND-ASSIGNED here and ignored on input (spec §2), so
//      nothing should send it. On a protocol the user picks it.
// ---------------------------------------------------------------------------

// True for the values that mean "no selection" rather than a real id. Spec §4:
// the backend treats 0 and -1 as nothing-selected, not as master row ids, so a
// select component's empty state is safe to pass through as long as a siteName
// rides along with it.
function isNoSiteId(value) {
  if (value === null || value === undefined || value === "") return true;
  const asNumber = Number(value);
  return Number.isNaN(asNumber) || asNumber === 0 || asNumber === -1;
}

// Reads `primarySites[]` off a parsed diagnosis payload.
//
// Returns null -- NOT [] -- when the key is absent, so the caller can tell
// "the server never told us" apart from "this diagnosis has no sites". That
// distinction is what keeps a save from sending a destructive [].
export function readDiagnosisPrimarySites(diagnosisObj) {
  if (!diagnosisObj || !Array.isArray(diagnosisObj.primarySites)) {
    return null;
  }

  return diagnosisObj.primarySites.map((site) => ({
    diagnosisPrimarySiteId: site.diagnosisPrimarySiteId,
    siteId: site.siteId,
    // Spec §6: render this, do not look siteId up in the picker options. A
    // diagnosis can reference a site that has since been retired from the
    // master, and the picker only returns active rows -- resolving against it
    // would blank a real value and lose it on the next save.
    siteName: site.siteName,
    leadSite: site.leadSite === true,
  }));
}

// Builds the request-body value: [{ diagnosisPrimarySiteId?, siteId?, siteName }].
//
// siteName is sent alongside siteId even though the id wins (spec §4) -- it
// costs nothing and makes a payload readable in devtools, which matters while
// this is being QA'd.
//
// An entry with neither an id nor a name is dropped here rather than sent: the
// backend rejects it, and that failure takes the WHOLE save down (spec §9).
export function buildDiagnosisPrimarySitesPayload(selectedSites) {
  if (!Array.isArray(selectedSites)) {
    return [];
  }

  const seen = new Set();
  const cleaned = [];

  selectedSites.forEach((site) => {
    if (!site) return;

    const name = typeof site.siteName === "string" ? site.siteName.trim() : "";
    const hasId = !isNoSiteId(site.siteId);

    if (!hasId && name === "") {
      return; // would fail the entire save
    }

    const key = hasId ? "id:" + Number(site.siteId) : "name:" + name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);

    const entry = {};
    if (site.diagnosisPrimarySiteId !== null && site.diagnosisPrimarySiteId !== undefined) {
      entry.diagnosisPrimarySiteId = site.diagnosisPrimarySiteId;
    }
    if (hasId) {
      entry.siteId = Number(site.siteId);
    }
    entry.siteName = name;

    // Deliberately no leadSite: the backend assigns it and ignores ours.
    cleaned.push(entry);
  });

  return cleaned;
}

// Attaches `primarySites` to a diagnosis DTO -- the single place that decides
// whether the key is sent at all.
//
// THE ONE THING THAT MUST NOT GO WRONG (spec §3). The key is three-valued:
//
//     key omitted   -> existing links left untouched
//     []            -> ALL links deleted for that diagnosis
//     [{...}]       -> link set replaced with exactly what was sent
//
// An accidental [] destroys data and still returns 200; the deprecated
// primarySite mirror clears too, so nothing looks wrong afterwards. Every other
// child collection on this DTO (biomarkers, tumorSizes, lymphNodes, ...) is
// spread in unconditionally and is [] when the user filled nothing in, which is
// exactly the shape that would wipe this one. Hence: never put primarySites in
// that object literal, call this instead.
//
// Exists as a standalone function so it can be tested -- prepareDataForAPI is a
// closure inside a 6,000-line component and cannot be imported.
export function attachDiagnosisPrimarySites(dto, selectedSites) {
  const payload = buildDiagnosisPrimarySitesPayload(selectedSites);
  if (payload.length > 0) {
    dto.primarySites = payload;
  }
  return dto;
}

// Builds the `siteIds` query-string value for fetchProtocolsByPrimarySite.
//
// Free-text sites (siteId null) are dropped: the protocol link table requires a
// real site, so they can never match anything (spec §7).
//
// Returns "" when nothing usable is left — the signal to OMIT the parameter
// rather than send an empty one. Both mean the same thing to the backend, but
// omitting is what the spec documents.
//
// Site 60 ("Not Found") is passed straight through even though the backend
// widens the whole query to every protocol when it sees it. That widening is
// deliberate — "Breast, plus something we could not classify" cannot safely be
// narrowed to breast protocols — so filtering 60 out here would silently
// re-narrow a result the backend intentionally opened up.
export function toSiteIdsParam(sites) {
  if (!Array.isArray(sites)) {
    return "";
  }

  const seen = new Set();
  const ids = [];

  sites.forEach((site) => {
    if (!site || isNoSiteId(site.siteId)) return;
    const id = Number(site.siteId);
    if (seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  });

  return ids.join(",");
}

// The name shown in the deprecated `primarySite` scalar: the lead entry's, or
// the first one's when nothing is flagged. Used to keep that mirror in step
// locally between saves, for the print view and the parent's narrative text.
export function leadSiteName(sites) {
  if (!Array.isArray(sites) || sites.length === 0) return "";
  const lead = sites.find((site) => site && site.leadSite === true) || sites[0];
  return (lead && lead.siteName) || "";
}

// Reconciles the selected set after the multi-select changes.
//
// `selectedIds` is what the control hands back (site ids, in selection order).
// Entries already selected keep their lead flag; new ones are hydrated from the
// option list. If the removed site was the lead, the flag MOVES to the first
// remaining site rather than being left dangling (spec §7).
export function syncSelection(prevSites, selectedIds, options) {
  const previous = Array.isArray(prevSites) ? prevSites : [];
  const available = Array.isArray(options) ? options : [];

  const next = (Array.isArray(selectedIds) ? selectedIds : []).map((rawId) => {
    const siteId = Number(rawId);
    const existing = previous.find((site) => Number(site.siteId) === siteId);
    if (existing) {
      return existing;
    }
    const option = available.find((site) => Number(site.siteId) === siteId);
    return {
      siteId: siteId,
      siteName: option ? option.siteName : String(rawId),
      tumorGroup: option ? option.tumorGroup : undefined,
      leadSite: false,
    };
  });

  if (next.length > 0 && !next.some((site) => site.leadSite)) {
    next[0] = { ...next[0], leadSite: true };
  }

  return next;
}
